import { useEffect, useState } from 'react';

import { useFormik } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';

import Button from 'components/Button/Button';

import { EToasterStatus, showToast } from '../../utils/showToast';

import { IMPORT_CSV_WRONG_SIZE_MESSAGE } from './constants/constants';
import { csvUploadSchema } from './constants/validationSchema';
import ImportCSVContent from './ImportCSVContent';
import { ImportCSVDrawerProps } from './types';

import './style.scss';

const ImportCSVDrawer = ({
    isOpen,
    onImportFile,
    onCloseDrawer,
    fileRef,
    fileKeyName,
    uploadType
}: ImportCSVDrawerProps) => {
    const [isUploading, setIsUploading] = useState(false);

    const { submitForm, resetForm, setFieldValue } = useFormik({
        validateOnMount: true,
        validationSchema: csvUploadSchema,
        enableReinitialize: true,
        initialValues: {
            file: null
        },
        onSubmit: async (_values: any, { resetForm }: any) => {
            const file = fileRef?.current?.files?.[0];
            const formData = new FormData();
            if (!file) return;
            if (file && file.size > 2000000) {
                showToast({
                    message: IMPORT_CSV_WRONG_SIZE_MESSAGE,
                    status: EToasterStatus.ERROR
                });
                return;
            }
            setIsUploading(true);

            formData.append(fileKeyName, file as any);

            onImportFile(formData);
            resetForm();
            setIsUploading(false);
        }
    });

    const handleClose = () => {
        resetForm();
        onCloseDrawer();
    };

    // Lock body scroll when drawer is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    return (
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-[9998]"
              onClick={handleClose}
            />

            {/* Drawer */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3, ease: 'easeInOut' }}
              className="fixed top-0 right-0 h-full w-[384px] bg-base-background shadow-2xl z-[9999] flex flex-col"
            >
              {/* Header */}
              <div className="flex items-start justify-between px-6 pt-16 pb-6 relative">
                <div className="flex-1">
                  <h2 className="text-[18px] font-semibold text-[var(--base-card-foreground,#09090B)] mb-2">
                    Upload CSV file
                  </h2>
                  <p className="text-sm text-base-mutedForeground">
                    Upload a CSV file with your{' '}
                    {uploadType === 'dynamic' ? 'dynamic' : 'static'} string
                    translations.
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="absolute top-6 right-6 text-base-mutedForeground hover:text-base-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-6">
                <ImportCSVContent
                  isUploading={isUploading}
                  fileRef={fileRef}
                  setFieldValue={setFieldValue}
                />
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 pb-6 pt-6">
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={() => submitForm()}
                  disabled={!fileRef?.current?.files?.length}
                >
                  Upload
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
};

export default ImportCSVDrawer;

